import { useEffect, useState, useCallback } from "react";
import {
  fetchMeetings,
  createMeeting,
  rescheduleMeeting,
  updateMeetingStatus,
  fetchBatchCurriculum,
} from "./adminCourseService";
import "./adminCourses.css";

const MEETING_STATUS_BADGE = {
  SCHEDULED:   "ac-badge-blue",
  LIVE:        "ac-badge-green",
  COMPLETED:   "ac-badge-purple",
  CANCELLED:   "ac-badge-red",
  RESCHEDULED: "ac-badge-orange",
};

/* valid next status actions per current status */
const STATUS_ACTIONS = {
  SCHEDULED:   [{ to: "LIVE",      label: "Go Live",       cls: "ac-btn-success" },
                { to: "CANCELLED", label: "Cancel",        cls: "ac-btn-danger"  }],
  LIVE:        [{ to: "COMPLETED", label: "Mark Complete", cls: "ac-btn-primary" },
                { to: "CANCELLED", label: "Cancel",        cls: "ac-btn-danger"  }],
  COMPLETED:   [],
  CANCELLED:   [],
  RESCHEDULED: [],
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
};

/* ── copy-to-clipboard button ── */
const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button className="ac-btn ac-btn-ghost ac-btn-sm" onClick={handle} title="Copy Zoom link">
      {copied ? "✓ Copied" : "Copy Link"}
    </button>
  );
};

/* ── create meeting modal ── */
const CreateMeetingModal = ({ curriculum, students, mode, onSave, onClose, saving }) => {
  const [curriculumId, setCurriculumId] = useState("");
  const [enrollmentId, setEnrollmentId] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [durationMins, setDurationMins] = useState(30);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      curriculumId: Number(curriculumId),
      enrollmentId: mode === "PERSONAL" && enrollmentId ? Number(enrollmentId) : null,
      scheduledAt: new Date(scheduledAt).toISOString(),
      durationMins: Number(durationMins),
    });
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
    }}>
      <div style={{
        background: "#fff", borderRadius: 10, padding: "1.5rem",
        width: "100%", maxWidth: 440, boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
        maxHeight: "90vh", overflowY: "auto",
      }}>
        <h3 style={{ margin: "0 0 1rem", fontSize: "1.1rem" }}>Schedule Meeting</h3>
        <form onSubmit={handleSubmit}>
          {/* lesson */}
          <div className="ac-form-group">
            <label>Lesson *</label>
            <select required value={curriculumId} onChange={(e) => setCurriculumId(e.target.value)}>
              <option value="">— Select lesson —</option>
              {curriculum.map((c) => (
                <option key={c.curriculumId ?? c.id} value={c.curriculumId ?? c.id}>
                  Day {c.dayNumber}: {c.title}{c.isAssessment ? " ★" : ""}
                </option>
              ))}
            </select>
          </div>

          {/* student — only for PERSONAL mode */}
          {mode === "PERSONAL" && (
            <div className="ac-form-group">
              <label>Student *</label>
              <select
                required
                value={enrollmentId}
                onChange={(e) => setEnrollmentId(e.target.value)}
              >
                <option value="">— Select student —</option>
                {students.map((s) => (
                  <option key={s.enrollmentId} value={s.enrollmentId}>
                    {s.userName}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* date/time + duration */}
          <div className="ac-form-row">
            <div className="ac-form-group">
              <label>Date & Time *</label>
              <input
                type="datetime-local"
                required
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
              />
            </div>
            <div className="ac-form-group">
              <label>Duration (mins)</label>
              <input
                type="number" min={1}
                value={durationMins}
                onChange={(e) => setDurationMins(e.target.value)}
              />
            </div>
          </div>

          <div className="ac-form-actions">
            <button type="submit" className="ac-btn ac-btn-success" disabled={saving}>
              {saving ? "Scheduling…" : "Schedule"}
            </button>
            <button type="button" className="ac-btn ac-btn-ghost" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── reschedule modal ── */
const RescheduleModal = ({ meeting, onSave, onClose, saving }) => {
  const [scheduledAt, setScheduledAt] = useState("");
  const [durationMins, setDurationMins] = useState(meeting.durationMins || 30);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(meeting.id, {
      scheduledAt: new Date(scheduledAt).toISOString(),
      durationMins: Number(durationMins),
    });
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
    }}>
      <div style={{
        background: "#fff", borderRadius: 10, padding: "1.5rem",
        width: "100%", maxWidth: 400, boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
      }}>
        <h3 style={{ margin: "0 0 0.25rem", fontSize: "1.1rem" }}>Reschedule Meeting</h3>
        <p style={{ fontSize: "0.82rem", color: "#6b7280", margin: "0 0 1rem" }}>
          Day {meeting.dayNumber}: {meeting.lessonTitle}
        </p>
        <form onSubmit={handleSubmit}>
          <div className="ac-form-row">
            <div className="ac-form-group">
              <label>New Date & Time *</label>
              <input
                type="datetime-local"
                required
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
              />
            </div>
            <div className="ac-form-group">
              <label>Duration (mins)</label>
              <input
                type="number" min={1}
                value={durationMins}
                onChange={(e) => setDurationMins(e.target.value)}
              />
            </div>
          </div>
          <div className="ac-form-actions">
            <button type="submit" className="ac-btn ac-btn-warning" disabled={saving}>
              {saving ? "Rescheduling…" : "Reschedule"}
            </button>
            <button type="button" className="ac-btn ac-btn-ghost" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── single meeting row ── */
const MeetingRow = ({ meeting, onStatusChange, onReschedule, actioning }) => {
  const actions = STATUS_ACTIONS[meeting.status] || [];
  const isRescheduled = meeting.status === "RESCHEDULED";

  return (
    <tr style={{ opacity: isRescheduled ? 0.55 : 1 }}>
      <td>
        <div style={{ textDecoration: isRescheduled ? "line-through" : "none" }}>
          <strong>Day {meeting.dayNumber}</strong>
          <div style={{ fontSize: "0.8rem", color: "#6b7280" }}>{meeting.lessonTitle}</div>
        </div>
      </td>
      <td style={{ fontSize: "0.85rem" }}>{fmtDateTime(meeting.scheduledAt)}</td>
      <td style={{ fontSize: "0.82rem" }}>{meeting.durationMins} min</td>
      <td>
        {meeting.zoomJoinUrl ? (
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <a
              href={meeting.zoomJoinUrl}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: "0.8rem", color: "#2563eb" }}
            >
              Open
            </a>
            <CopyBtn text={meeting.zoomJoinUrl} />
          </div>
        ) : (
          <span style={{ color: "#9ca3af", fontSize: "0.82rem" }}>Not set</span>
        )}
      </td>
      <td>
        <span className={`ac-badge ${MEETING_STATUS_BADGE[meeting.status] || "ac-badge-gray"}`}>
          {meeting.status}
        </span>
        {isRescheduled && meeting.rescheduledToId && (
          <div style={{ fontSize: "0.72rem", color: "#6b7280", marginTop: "0.2rem" }}>
            → New meeting #{meeting.rescheduledToId}
          </div>
        )}
      </td>
      <td>
        <div className="ac-actions">
          {actions.map(({ to, label, cls }) => (
            <button
              key={to}
              className={`ac-btn ${cls} ac-btn-sm`}
              disabled={actioning === meeting.id}
              onClick={() => onStatusChange(meeting.id, to)}
            >
              {label}
            </button>
          ))}
          {!isRescheduled && meeting.status !== "COMPLETED" && meeting.status !== "CANCELLED" && (
            <button
              className="ac-btn ac-btn-warning ac-btn-sm"
              disabled={actioning === meeting.id}
              onClick={() => onReschedule(meeting)}
            >
              Reschedule
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

/* ─────────────────────────────────────────────
   Main tab component
───────────────────────────────────────────── */
const AdminMeetingsTab = ({ courseId, batchId, mode, students }) => {
  const [meetings, setMeetings] = useState([]);
  const [curriculum, setCurriculum] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actioning, setActioning] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meetRes, currRes] = await Promise.all([
        fetchMeetings(courseId, batchId),
        fetchBatchCurriculum(batchId),
      ]);
      // sort by scheduledAt ascending
      const sorted = (meetRes.data || []).sort(
        (a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt)
      );
      setMeetings(sorted);
      // filter out hidden lessons, sort by day
      const currSorted = (currRes.data || [])
        .filter((c) => !c.isHidden)
        .sort((a, b) => (a.dayNumber ?? 0) - (b.dayNumber ?? 0));
      setCurriculum(currSorted);
    } catch {
      setError("Failed to load meetings.");
    } finally {
      setLoading(false);
    }
  }, [courseId, batchId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (data) => {
    setSaving(true);
    setError("");
    try {
      await createMeeting(courseId, batchId, data);
      setShowCreate(false);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Failed to schedule meeting.";
      setError(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleReschedule = async (meetingId, data) => {
    setSaving(true);
    setError("");
    try {
      await rescheduleMeeting(courseId, batchId, meetingId, data);
      setRescheduleTarget(null);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Reschedule failed.";
      setError(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (meetingId, to) => {
    setActioning(meetingId);
    setError("");
    try {
      await updateMeetingStatus(courseId, batchId, meetingId, to);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || "Status update failed.";
      setError(String(msg));
    } finally {
      setActioning(null);
    }
  };

  /* group by student for PERSONAL mode */
  const grouped = mode === "PERSONAL"
    ? (students || []).map((s) => ({
        student: s,
        meetings: meetings.filter((m) => m.enrollmentId === s.enrollmentId),
      }))
    : null;

  if (loading) return <div className="ac-loading">Loading meetings…</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "0.75rem" }}>
        <button
          className="ac-btn ac-btn-primary"
          onClick={() => setShowCreate(true)}
        >
          + Schedule Meeting
        </button>
      </div>

      {error && (
        <div style={{
          background: "#fee2e2", color: "#b91c1c",
          padding: "0.6rem 0.9rem", borderRadius: "6px",
          marginBottom: "1rem", fontSize: "0.88rem",
        }}>
          {error}
        </div>
      )}

      {/* ── BATCH mode: flat table ── */}
      {mode !== "PERSONAL" && (
        <>
          {meetings.length === 0 ? (
            <div className="ac-empty">No meetings scheduled yet.</div>
          ) : (
            <div className="ac-table-wrap">
              <table className="ac-table">
                <thead>
                  <tr>
                    <th>Lesson</th>
                    <th>Scheduled At</th>
                    <th>Duration</th>
                    <th>Zoom Link</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {meetings.map((m) => (
                    <MeetingRow
                      key={m.id}
                      meeting={m}
                      onStatusChange={handleStatusChange}
                      onReschedule={setRescheduleTarget}
                      actioning={actioning}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── PERSONAL mode: grouped by student ── */}
      {mode === "PERSONAL" && (
        <>
          {(!grouped || grouped.length === 0) ? (
            <div className="ac-empty">No students enrolled yet.</div>
          ) : (
            grouped.map(({ student, meetings: sm }) => (
              <div key={student.enrollmentId} style={{ marginBottom: "1.5rem" }}>
                <div style={{
                  fontWeight: 600, fontSize: "0.9rem", color: "#374151",
                  padding: "0.4rem 0", borderBottom: "1px solid #e5e7eb",
                  marginBottom: "0.5rem",
                }}>
                  {student.userName}
                  <span style={{ fontWeight: 400, color: "#9ca3af", marginLeft: "0.5rem", fontSize: "0.82rem" }}>
                    {sm.length} meeting{sm.length !== 1 ? "s" : ""} scheduled
                  </span>
                </div>
                {sm.length === 0 ? (
                  <div style={{ color: "#9ca3af", fontSize: "0.85rem", padding: "0.5rem 0" }}>
                    No meetings scheduled for this student.
                  </div>
                ) : (
                  <div className="ac-table-wrap">
                    <table className="ac-table">
                      <thead>
                        <tr>
                          <th>Lesson</th>
                          <th>Scheduled At</th>
                          <th>Duration</th>
                          <th>Zoom Link</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sm.map((m) => (
                          <MeetingRow
                            key={m.id}
                            meeting={m}
                            onStatusChange={handleStatusChange}
                            onReschedule={setRescheduleTarget}
                            actioning={actioning}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))
          )}
        </>
      )}

      {/* ── modals ── */}
      {showCreate && (
        <CreateMeetingModal
          curriculum={curriculum}
          students={students || []}
          mode={mode}
          onSave={handleCreate}
          onClose={() => setShowCreate(false)}
          saving={saving}
        />
      )}

      {rescheduleTarget && (
        <RescheduleModal
          meeting={rescheduleTarget}
          onSave={handleReschedule}
          onClose={() => setRescheduleTarget(null)}
          saving={saving}
        />
      )}
    </div>
  );
};

export default AdminMeetingsTab;
